import { Fragment, useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import dynamic from "next/dynamic";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import useShowNewHeader from "@hooks/use-show-new-header";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import ShimmerWrapper from "@stadium/ui/src/components/loaders/shimmer-wrapper";
import Multimedia from "@sharedComponents/multimedia";
import GlobalProductSearch from "@sharedComponents/global-search/global-product-search";

import { LISTING_SECTION_REF } from "@components/store/products/listing";

import { getCountry } from "@store/selectors/country";
import {
  getStoreCloneId,
  getStoreID,
  getStoreParentId,
  getStoreSyncedToES,
  getCommonElasticSearchStoreVariables,
  getIsBundleShop,
  getIsLockerShop,
  getIsLegacyLockerShop,
  getIsFiltersOnLeft,
  getDesignedStoreIds,
  getIsSinglePageShop,
  getStoreNumber,
} from "@store/selectors/store";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getMenuAllChildren } from "@selectors/menu";
import { getAllMenuTaxons } from "@helpers/menu";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import Product from "@sharedComponents/cards/product";
import ProductListingHeader from "@components/store/products/listing-header";
import Sidebar from "@components/store/sidebar";
import ListingPagination from "@components/store/products/listing-pagination";
import ScrollToTopBtn from "@stadium/ui/src/components/scroll-to-top-button";
import Portal from "@stadium/ui/src/components/portal";

import useSelectedCountriesIsos from "@hooks/search/use-selected-countries-isos";
import useSearchProductList from "@hooks/search/use-fetch-base-product-list";
import { useStoreContext } from "@components/store/provider";
import useSetFilters from "@hooks/search/use-set-filter";

import ShadowCard from "@sharedComponents/cards/shadow-card";
import { BackButton } from "@sharedComponents/buttons";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { getStadiumCatalogNumber } from "@selectors/config";
import { STICKY_HEADER_REF } from "@stadium/ui/src/components/header";

import {
  ADDITIONAL_ON_DEMAND_FILTER_TYPE,
  BULK_ITEM_FILTER_TYPE,
  CORE_ON_DEMAND_FILTER_TYPE,
  ECO_FRIENDLY_FILTER,
  BRAND_FILTER_KEY,
  ON_DEMAND_FILTER_TYPE,
  SHIPPED_FROM_STADIUM_FILTER,
  SUSTAINABLE_FILTER,
  VOLUME_DISCOUNT_FILTER,
} from "@constants/products/listing";
import {
  MENU_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME_OLD,
  PRODUCT_VIEW_PATHNAME,
  SEARCH_PAGE_PATHNAME,
  getMenuRoute,
} from "@constants/routes";
import {
  OLD_SEARCH_PAGE_PATHNAME,
  OLD_PRODUCT_VIEW_PATHNAME,
  OLD_MENU_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
} from "@constants/old-routes";

import notFoundImg from "@images/svg/no-result.svg";

import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_PAGINATION } from "@constants/analytics/filter";

import ListingScrollTracker from "@components/store/products/listing-scroll-tracker";

import styles from "../main.module.scss";

const BrandBanner = dynamic(() => import("@sharedComponents/banner/brand"));

const ALLOWED_BACK_BUTTON_ROUTES = [
  SEARCH_PAGE_PATHNAME,
  OLD_SEARCH_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME_OLD,
  OLD_MENU_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
];
const SKIPPED_BACK_BUTTON_ROUTES = [PRODUCT_VIEW_PATHNAME, OLD_PRODUCT_VIEW_PATHNAME];

const NoProductsView = ({ searchedFor, isSwagCreationShopFlow }) => {
  return (
    <div className={styles.noProductsView}>
      <Multimedia src={notFoundImg} type="image" width={138} height={138} alt="No results found" />
      <div className={styles.noResultFound}>
        No results found under <strong>{searchedFor}</strong>.
        <div className={styles.resetFilter}>
          {" "}
          Try resetting your Search{isSwagCreationShopFlow ? " and preferred swag colors" : ""}.
        </div>
      </div>
    </div>
  );
};

const ShimmerLoader = ({ productCardClass }) => (
  <ShimmerWrapper length={8} cardClass={productCardClass} wrapperClass={styles.rowWrapper}>
    <ShadowCard />
  </ShimmerWrapper>
);

const ProductSearchList = ({ searchQuery = "", brand, searchedFor }) => {
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const { currentPage, currentSortBy, setPage, perPage, setPerPage } =
    useContext(ProductListingContext);

  const router = useRouter();
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeCloneId = useSelector(getStoreCloneId);
  const allChildren = useSelector(getMenuAllChildren);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isBundleShop = useSelector(getIsBundleShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const showNewHeader = useShowNewHeader();

  const { storeBasePath, isGlobalCountry } = useStoreContext();
  const { getStoreUpdatedAt } = usePreviewModeContext();
  const productCardClass = styles.colCard;

  const shopAnalyticsContext = useShopAnalyticsContext();

  if (!searchQuery && isEmpty(brand)) router.push(storeBasePath);

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag } = useMemo(
    () => getAllMenuTaxons(allChildren),
    [allChildren],
  );

  const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
    country,
    isGlobalCountry,
    countryKeyAttr: "countryCode",
  });

  const queryVariables = useMemo(() => {
    return {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      taxonIds,
      page: +currentPage,
      perPage,
      sortBy: currentSortBy,
      searchQuery,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      withSwag: anyMenuWithSwag,
      storeUpdatedAt: getStoreUpdatedAt(),
      showVariantsAsProducts: true,
      ...(isBundleShop && { bulkAvailable: false, withOutOfStock: true, withSwag: true }),
      ...commonESVariables,
      ...commonGlobalCountryVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
    };
  }, [
    storeParentId,
    storeId,
    country,
    currentPage,
    currentSortBy,
    searchQuery,
    taxonIds,
    anyMenuWithSwag,
    commonESVariables,
    perPage,
    pricelessVariables,
  ]);

  const { loading, fetchedProducts, totalProducts, getProductList } = useSearchProductList();

  useEffect(() => {
    if (storeParentId && taxonIds.length)
      getProductList({
        variables: queryVariables,
      });
  }, [storeParentId, currentSortBy, currentPage, searchQuery, taxonIds, perPage]);

  const renderProducts = () => {
    if ((loading && fetchedProducts.length === 0) || taxonIds.length === 0)
      return <ShimmerLoader productCardClass={productCardClass} />;
    if (isEmpty(fetchedProducts))
      return (
        <NoProductsView searchedFor={searchedFor} isSwagCreationShopFlow={isSwagCreationShopFlow} />
      );
    return (
      <ListingScrollTracker baseEventProperties={shopAnalyticsContext}>
        <article className={`${styles.rowWrapper} ${styles.productCardsWrapper}`}>
          {fetchedProducts.map((product, index) => (
            <Fragment key={`product-${product.id}`}>
              <Product
                data={product}
                key={product.id}
                source="search"
                productTileClass="white-base-tile"
                className={productCardClass}
                imageLoadingStrategy="lazy"
                eventLocationAnalytics={{
                  track: true,
                  eventProperties: {
                    ...shopAnalyticsContext,
                    product_id: product.id,
                    product_name: product.name,
                    position_in_list: index + 1,
                  },
                }}
              />
            </Fragment>
          ))}
        </article>
      </ListingScrollTracker>
    );
  };

  return (
    <section ref={LISTING_SECTION_REF} className={styles.fullwidthBg}>
      <div
        className={`${styles.searchPageContainer} ${
          showNewHeader ? "" : styles.searchPageContainerPadding
        }`}
      >
        <ProductListingHeader
          totalProducts={totalProducts}
          currentPage={currentPage}
          perPage={perPage}
          showFilters={false}
          source="search"
          searchedFor={searchedFor}
          showSortDropdown={!isEmpty(fetchedProducts)}
        />
        <ScrollToTopBtn />
        {renderProducts()}
        <ListingPagination
          total={totalProducts}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={setPage}
          className="pagination"
          showPerPageDropdown
          setPerPage={setPerPage}
          eventLocationAnalytics={{
            track: true,
            target: ANALYTICS_TARGET_PAGINATION,
            action: ANALYTICS_EVENT_ACTION_CHANGED,
            eventProperties: shopAnalyticsContext,
          }}
        />
      </div>
    </section>
  );
};

const ProductSearchListWithFilters = ({ searchQuery, brand, searchedFor }) => {
  const { isSwagCreationShopFlow, preferredColorNames, currentUserConfig } = useCreateSwagContext();
  const { isDesigner = false } = currentUserConfig || {};
  const {
    currentDietaryRestrictionsIds,
    currentPage,
    currentPriceRangeIds,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    currentDecorationMethods,
    currentSortBy,
    setPage,
    customizable,
    setIsFetchingProducts,
    flattenedBrandValueIds,
    selectedProductionDays,
    selectedItemFeatures,
    setTotalProducts,
    itemType,
    isFetchingProducts,
    perPage,
    setPerPage,
    selectedItemTypes,
    currentCountryIds,
  } = useContext(ProductListingContext);

  const { selectedCountryIsos } = useSelectedCountriesIsos({ currentCountryIds });

  const { isMobileDevice, isDesktopDevice } = useContext(CurrentDeviceContext);

  const router = useRouter();
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeCloneId = useSelector(getStoreCloneId);
  const allChildren = useSelector(getMenuAllChildren);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isBundleShop = useSelector(getIsBundleShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const designedStoreIds = useSelector(getDesignedStoreIds);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const { storeBasePath, isGlobalCountry, isPreviewCatalogMode } = useStoreContext();
  const { getStoreUpdatedAt } = usePreviewModeContext();
  const productCardClass = styles.colCard;

  const isFiltersOnLeft = useSelector(getIsFiltersOnLeft);
  const showLeftFilter = !isMobileDevice && isFiltersOnLeft;
  const menuRoute = getMenuRoute({ storeBasePath });

  if (!searchQuery && isEmpty(brand)) router.push(storeBasePath);

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag } = useMemo(
    () => getAllMenuTaxons(allChildren),
    [allChildren],
  );

  const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
    country,
    isGlobalCountry,
    countryKeyAttr: "countryCode",
  });

  const preferredColorPresentationsDependencyKey = preferredColorNames
    ?.map(({ presentation }) => presentation)
    .join();
  const selectedCountryIsosDependencyKey = selectedCountryIsos?.join();

  const queryVariables = useMemo(() => {
    const isCoreOnDemandFilterSelected = selectedItemTypes.includes(CORE_ON_DEMAND_FILTER_TYPE.id);
    const isAdditionalOnDemandFilterSelected = selectedItemTypes.includes(
      ADDITIONAL_ON_DEMAND_FILTER_TYPE.id,
    );
    const noMinQuantity =
      (isCoreOnDemandFilterSelected && isAdditionalOnDemandFilterSelected) ||
      selectedItemTypes.includes(ON_DEMAND_FILTER_TYPE.id);
    const bulkProducts = selectedItemTypes.includes(BULK_ITEM_FILTER_TYPE.id);
    const hasTierPricing = selectedItemTypes.includes(VOLUME_DISCOUNT_FILTER.id);
    const coreOnDemand = !noMinQuantity && isCoreOnDemandFilterSelected;
    const additionalOnDemand = !noMinQuantity && isAdditionalOnDemandFilterSelected;
    const ecoFriendly = selectedItemFeatures?.some((id) => id === ECO_FRIENDLY_FILTER.id);
    const sustainable = selectedItemFeatures?.some((id) => id === SUSTAINABLE_FILTER.id);
    const shippedFromStadium = selectedItemFeatures?.some(
      (id) => id === SHIPPED_FROM_STADIUM_FILTER.id,
    );

    const variables = {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      taxonIds,
      page: +currentPage,
      perPage,
      sortBy: currentSortBy,
      searchQuery,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      withSwag: anyMenuWithSwag,
      storeUpdatedAt: getStoreUpdatedAt(),
      showVariantsAsProducts: true,
      productionDays: selectedProductionDays,
      dietaryRestrictionIds: currentDietaryRestrictionsIds.map((id) => +id),
      brandIds: !isEmpty(brand) ? [+brand.id] : flattenedBrandValueIds,
      priceKeys: currentPriceRangeIds,
      color: currentColorOptionValueIds,
      size: currentSizeOptionValueIds,
      coreOnDemand,
      additionalOnDemand,
      noMinQuantity,
      bulkProducts,
      ecoFriendly,
      printTypes: currentDecorationMethods,
      ...(shippedFromStadium && { shippedFromStadium }),
      ...(sustainable && { sustainable }),
      ...(isBundleShop && { bulkAvailable: false, withOutOfStock: true, withSwag: true }),
      ...commonESVariables,
      ...commonGlobalCountryVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
    };

    if (hasTierPricing) {
      variables.hasTierPricing = true;
    }

    if (isSwagCreationShopFlow) {
      variables.color = preferredColorNames?.map(({ presentation }) => presentation) || [];
      variables.countryCodes = selectedCountryIsos;
      variables.includeNonColorProducts = true;
      variables.countryCode = "";
      variables.designer = isDesigner;
    }

    if (isLockerShop) {
      variables.withSwag = true;
      variables.optimizedSearch = true;
    }
    return variables;
  }, [
    brand,
    storeParentId,
    storeId,
    taxonIds,
    currentPage,
    perPage,
    currentSortBy,
    searchQuery,
    syncedToEs,
    storeCloneId,
    anyMenuWithSwag,
    getStoreUpdatedAt,
    selectedProductionDays,
    currentDietaryRestrictionsIds,
    flattenedBrandValueIds,
    currentPriceRangeIds,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    isBundleShop,
    commonESVariables,
    commonGlobalCountryVariables,
    isLockerShop,
    designedStoreIds,
    customizable,
    selectedItemFeatures,
    perPage,
    selectedItemTypes,
    isSwagCreationShopFlow,
    preferredColorNames,
    selectedCountryIsos,
    isDesigner,
  ]);

  const { loading, fetchedProducts, totalProducts, getProductList, aggregations } =
    useSearchProductList();

  useSetFilters({ loading, aggregations });

  useEffect(() => {
    setIsFetchingProducts(loading);
  }, [loading, setIsFetchingProducts]);

  useEffect(() => {
    setTotalProducts(totalProducts);
  }, [totalProducts]);

  useEffect(() => {
    if (storeParentId && taxonIds.length)
      getProductList({
        variables: queryVariables,
      });
  }, [
    storeParentId,
    currentSortBy,
    currentPage,
    searchQuery,
    taxonIds,
    flattenedBrandValueIds,
    customizable,
    selectedProductionDays,
    currentDietaryRestrictionsIds,
    currentPriceRangeIds,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    currentDecorationMethods,
    itemType,
    selectedItemFeatures,
    perPage,
    selectedItemTypes,
    isSwagCreationShopFlow,
    preferredColorPresentationsDependencyKey,
    selectedCountryIsosDependencyKey,
    isDesigner,
  ]);

  const showNewHeader = useShowNewHeader();

  const storeNumber = useSelector(getStoreNumber);
  const { withMenu } = useStoreContext();
  const isSinglePageShop = useSelector(getIsSinglePageShop);
  const catalogShopNumber = useConfig(getStadiumCatalogNumber);
  const showMenu =
    storeNumber && withMenu && catalogShopNumber !== storeNumber && !isSinglePageShop;

  const shopAnalyticsContext = useShopAnalyticsContext();

  const renderSideBar = () => {
    if (showLeftFilter) {
      const hideFilterKeys = isEmpty(brand) ? [] : [BRAND_FILTER_KEY];
      return (
        <div className={styles.colLeftSideBar}>
          <div className={`${styles.leftSideBar} ${showMenu ? "" : styles.withoutMenu} `}>
            <Sidebar
              isMultiLevelMenu={false}
              showLeftFilter={showLeftFilter}
              hideFilterKeys={hideFilterKeys}
            />
          </div>
        </div>
      );
    }
  };

  const renderProducts = () => {
    if (loading || taxonIds.length === 0)
      return <ShimmerLoader productCardClass={productCardClass} />;
    if (isEmpty(fetchedProducts))
      return (
        <NoProductsView searchedFor={searchedFor} isSwagCreationShopFlow={isSwagCreationShopFlow} />
      );
    return (
      <ListingScrollTracker baseEventProperties={shopAnalyticsContext}>
        <article className={`${styles.rowWrapper} ${styles.productCardsWrapper}`}>
          {fetchedProducts.map((product, index) => (
            <Fragment key={`product-${product.id}`}>
              <Product
                data={product}
                key={product.id}
                source="search"
                productTileClass="white-base-tile"
                className={productCardClass}
                imageLoadingStrategy="lazy"
                eventLocationAnalytics={{
                  track: true,
                  eventProperties: {
                    ...shopAnalyticsContext,
                    product_id: product.id,
                    product_name: product.name,
                    position_in_list: index + 1,
                  },
                }}
              />
            </Fragment>
          ))}
        </article>
      </ListingScrollTracker>
    );
  };

  const renderProductsContainer = () => {
    const className = showLeftFilter ? styles["colRightSideBar"] : styles["colWithoutLeftSidebar"];
    return (
      <article
        className={`${className} ${styles.searchProductsContainer} ${
          showNewHeader ? "" : styles.searchTopPadding
        } ${isSwagCreationShopFlow ? styles.withSwagSearchFlow : ""}`}
      >
        {!isDesktopDevice && !showNewHeader && (
          <BackButton
            allowedRoutes={ALLOWED_BACK_BUTTON_ROUTES}
            skipRoutes={SKIPPED_BACK_BUTTON_ROUTES}
            appendQueryParams
            fallbackRoute={menuRoute}
          >
            BACK TO MENU
          </BackButton>
        )}
        <div className={styles.listWrapper}>
          {isMobileDevice && isPreviewCatalogMode && showNewHeader && (
            <GlobalProductSearch isCatalogSearchPage />
          )}
          {!isEmpty(brand) && !showNewHeader && <BrandBanner brand={brand} />}
          <section className={styles.pageSection}>
            <ProductListingHeader
              totalProducts={totalProducts}
              currentPage={currentPage}
              perPage={perPage}
              showFilters={!showLeftFilter}
              source="search"
              searchedFor={isEmpty(brand) ? searchedFor : ""}
              showSortDropdown={!isEmpty(fetchedProducts)}
              title={isMobileDevice ? "" : brand?.name}
              isFetchingProducts={loading}
            />
            <ScrollToTopBtn />
            {renderProducts()}
            {!isFetchingProducts && (
              <ListingPagination
                total={totalProducts}
                perPage={perPage}
                currentPage={currentPage}
                onPageChange={setPage}
                className="pagination"
                showPerPageDropdown
                setPerPage={setPerPage}
                eventLocationAnalytics={{
                  track: true,
                  target: ANALYTICS_TARGET_PAGINATION,
                  action: ANALYTICS_EVENT_ACTION_CHANGED,
                  eventProperties: shopAnalyticsContext,
                }}
              />
            )}
          </section>
        </div>
      </article>
    );
  };

  return (
    <section
      className={`${styles.fullwidthBg} ${styles.onlyListingPage}`}
      ref={LISTING_SECTION_REF}
    >
      {isMobileDevice && showNewHeader && !isPreviewCatalogMode && brand?.name && (
        <Portal id="brand-page-header-container" dataContainerId="brand-page-header">
          <div
            className={styles.responsiveSubCategorySection}
            style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
          >
            <h2 className={styles.pageListTitle}>{brand?.name}</h2>
          </div>
          <div
            className={styles.responsiveBrandTitleSpacing}
            style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
          />
        </Portal>
      )}
      <div className={styles.storePageContainer}>
        <div className={`${styles.rowWrapper} ${styles.rowWrapperMain}`}>
          {renderSideBar()}
          {renderProductsContainer()}
        </div>
      </div>
    </section>
  );
};

const WrappedSearch = (props) => {
  const isBundleShop = useSelector(getIsBundleShop);
  if (isBundleShop) {
    return <ProductSearchList {...props} />;
  }
  return <ProductSearchListWithFilters {...props} />;
};

export default WrappedSearch;
